import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { isEmpty, first } from 'lodash';
import { getClientCampaigns, resetCampaigns } from 'redux/actions/campaignActions';
import CircularProgress from '@material-ui/core/CircularProgress';
import Avatar from '@material-ui/core/Avatar';
import { withStyles } from '@material-ui/core/styles';
import Analytics from 'icons/analytics';
import CheckRoundedIcon from '@material-ui/icons/CheckRounded';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import DashboardRoundedIcon from '@material-ui/icons/DashboardRounded';
import CampaignOutlined from 'icons/campaignOutlined';
import HitIcon from 'icons/hit';
import { ReactComponent as OpportunityIcon } from 'icons/opportunity.svg';
import KeyboardArrowDownRoundedIcon from '@material-ui/icons/KeyboardArrowDownRounded';
import { getFullname, calculatePercentage } from 'utils/misc';
import Popover from 'components/Popover/Popover';
import ProfileMenuItems from 'components/ProfileMenuItems';
import { MIN_DESKTOP_WIDTH } from 'constants/misc';
import logo from 'images/logo/pr-volt-white.png';
import { Can, AbilityContext } from 'utils/permissions';
import { OPEN_PROFILE_MENU } from 'constants/analytics';
import { ReactComponent as AccountIcon } from 'icons/accounts.svg';
import { getSequences } from 'redux/actions/sequenceActions';
import CampaignCreate from 'containers/Campaigns/CampaignList/CampaignCreate';
import classNames from 'classnames';
import SidebarLink from './SidebarLink';
import withResponsive from '../withResponsive';
import { logout } from '../../../redux/actions/userActions';
import { analyticsTrack } from '../../../utils/analytics';
import { useCountContextCountRotting } from '../../CountRottingLeads';

const ANALYTICS_PATH = '/reports';
const DASHBOARD_PATH = '/dashboard';
const SEQUENCE_PATH = '/sequences';
const CAMPAIGNS_PATH = '/campaigns';

const styles = theme => ({
  root: {
    height: '2rem',
    width: '2rem',
    marginLeft: '-0.5rem',
  },
  sidebar__content: {
    paddingTop: '0.938rem',
    height: '100%',
    overflow: 'hidden',
    position: 'relative',
    '&.collapsed': {
      width: '4rem',
      overflow: 'hidden',
      transition: 'width 0.3s',
      '& $sidebar__image': {
        margin: '2.5rem 0 0.75rem 0.563rem',
        transition: 'all 0.3s',
      },
      '& $sidebar__campaign': {
        transition: 'all 0s',
        opacity: '0',
      },
      '& $sidebar__campaign__create': {
        margin: '1rem 0 4rem 0',
        '& $createButtonText': {
          fontSize: 0,
        },
      },
    },
    [`@media screen and (max-width: ${MIN_DESKTOP_WIDTH}px)`]: {
      marginTop: '5rem',
      '& $sidebar__image': {
        display: 'none',
      },
      '& $sidebar__campaign__create': {
        margin: '1rem 0 0 0',
      },
    },
  },
  sidebar__image: {
    margin: '2.5rem 0 0.75rem 1.25rem',
    transition: 'all 0.5s',
    transitionDelay: '0.3s',
  },
  sidebar__block: {
    padding: '0.938rem 0',
    '&:last-child': {
      border: 'none',
    },
  },
  sidebar__campaign__create: {
    margin: '1rem 1rem 4rem 1rem',
  },
  sidebar__campaign: {
    position: 'absolute',
    bottom: '1.5rem',
    padding: '0 1rem',
    width: '100%',
    transition: 'all 0.5s',
    transitionDelay: '0.3s',
    opacity: '1',
  },
  siderbar__campaign__current: {
    fontSize: '0.75rem',
    fontWeight: '600',
    color: theme.palette.secondary['0'],
    lineHeight: '1rem',
    marginBottom: '0.563rem',
  },
  siderbar__profile__block: {
    [`@media screen and (min-width: ${MIN_DESKTOP_WIDTH}px)`]: {
      display: 'none !important',
    },
  },
  siderbar__campaign__block: {
    backgroundColor: theme.palette.secondary.light,
    borderRadius: '0.375rem',
    minHeight: '3.5rem',
    height: 'auto',
    display: 'flex',
    alignItems: 'center',
    padding: '0 1rem',
    marginBottom: '1rem',
    position: 'relative',
    '&:last-child': {
      margin: '0',
    },
  },
  sidebar__campaign__name: {
    marginLeft: '0.75rem',
    color: theme.palette.secondary['0'],
  },
  avatarDropdownIcon: {
    position: 'absolute',
    right: '1rem',
    color: theme.palette.white,
  },
});


class SidebarContent extends Component {
  static propTypes = {
    onClick: PropTypes.func.isRequired,
    logout: PropTypes.func.isRequired,
    classes: PropTypes.shape({}).isRequired,
    collapsed: PropTypes.bool.isRequired,
    user: PropTypes.shape({
      full_name: PropTypes.string,
    }).isRequired,
    getClientCampaigns: PropTypes.func.isRequired,
    getSequences: PropTypes.func.isRequired,
    resetCampaigns: PropTypes.func.isRequired,
    campaign: PropTypes.shape({
      id: PropTypes.number,
      account: PropTypes.number,
      name: PropTypes.string,
      loading: PropTypes.bool,
      loaded: PropTypes.bool,
    }).isRequired,
    campaigns: PropTypes.shape({
      loading: PropTypes.bool,
      loaded: PropTypes.bool,
      count: PropTypes.number,
      next: PropTypes.string,
      previous: PropTypes.string,
      results: PropTypes.array,
    }).isRequired,
    sequences: PropTypes.shape({
      loading: PropTypes.bool,
      loaded: PropTypes.bool,
      count: PropTypes.number,
      next: PropTypes.string,
      previous: PropTypes.string,
      results: PropTypes.array,
    }).isRequired,
    accounts: PropTypes.shape({
      loading: PropTypes.bool,
      loaded: PropTypes.bool,
      count: PropTypes.number,
      next: PropTypes.string,
      previous: PropTypes.string,
      results: PropTypes.array,
    }).isRequired,
  };

  constructor() {
    super();
    this.state = {
      anchorEl: null,
      campaign: null,
    };
  }

  componentDidMount() {
    if (this.context.can('see', 'Campaign Selector')) {
      this.props.getClientCampaigns({ status: true });
    }
  }

  componentDidUpdate() {
    const { campaigns } = this.props;
    const { campaign } = this.state;

    if (!campaign && !isEmpty(campaigns.results)) {
      const newCampaign = first(campaigns.results);
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ campaign: newCampaign });
      this.props.getSequences(newCampaign.id);
    }
  }

  componentWillUnmount() {
    this.props.resetCampaigns();
  }

  hideSidebar = () => {
    this.props.onClick();
  };

  handleLogout = () => {
    this.props.logout();
  };

  handleProfileClick = (event) => {
    analyticsTrack(OPEN_PROFILE_MENU, {
      open: true,
    }, true);
    this.setState({ anchorEl: event.currentTarget });
  }

  handleProfileClose = () => {
    analyticsTrack(OPEN_PROFILE_MENU, {
      open: false,
    }, true);
    this.setState({ anchorEl: null });
  }

  render() {
    const {
      classes, collapsed, user, sequences,
    } = this.props;
    const { anchorEl, campaign } = this.state;

    const activeSequences = sequences.results.filter(seq => seq.status);
    const progress = activeSequences.reduce((acum, curr) => (acum + parseFloat(curr.stat_progress)), 0);
    const progressPct = calculatePercentage(activeSequences.length, progress);

    return (
      <div className={classNames(classes.sidebar__content, { collapsed })}>
        <img className={classes.sidebar__image} src={logo} width="156" alt="prvolt logo" />
        <Can I="create" a="Campaign">
          <CampaignCreate
            className={`${classes.sidebar__campaign__create}`}
            collapsed={collapsed}
          />
        </Can>
        <ul className={classes.sidebar__block}>
          <SidebarLink
            title="Dashboard"
            icon={DashboardRoundedIcon}
            route="/dashboard"
            onClick={this.hideSidebar}
            collapsed={collapsed}
            isActive={(match, location) => (
              location.pathname.startsWith(DASHBOARD_PATH) ||
              (this.context.can('see', 'Campaign Selector') ? null : location.pathname === '/')
            )}
            dataIntercomTarget="Dashboard sidebar button"
          />
          <Can I="see" a="Campaign">
            <SidebarLink
              title="Campaigns"
              icon={CampaignOutlined}
              route="/campaigns"
              onClick={this.hideSidebar}
              collapsed={collapsed}
              isActive={(match, location) => (
                location.pathname.startsWith(SEQUENCE_PATH) ||
                location.pathname.startsWith(CAMPAIGNS_PATH)
              )}
              dataIntercomTarget="Campaigns sidebar button"
            />
          </Can>

          <Can I="see" a="Inbox">
            <SidebarLink
              title="Inbox"
              icon={MailOutlineIcon}
              number={null}
              route="/inbox"
              onClick={this.hideSidebar}
              collapsed={collapsed}
              dataIntercomTarget="Inbox sidebar button"
            />
          </Can>
          <Can I="see" a="Opportunity">
            <SidebarLinkCountRotting
              title="Opportunities"
              icon={OpportunityIcon}
              route="/opportunities"
              onClick={this.hideSidebar}
              collapsed={collapsed}
              dataIntercomTarget="Opportunities sidebar button"
            />
          </Can>
          <Can I="see" a="Coverage">
            <SidebarLink
              title="Coverage"
              icon={HitIcon}
              route="/coverage"
              onClick={this.hideSidebar}
              collapsed={collapsed}
              dataIntercomTarget="Coverage sidebar button"
            />
          </Can>
          <SidebarLink
            title="Analytics"
            icon={Analytics}
            route="/reports"
            onClick={this.hideSidebar}
            collapsed={collapsed}
            isActive={(match, location) => (
              location.pathname.startsWith(ANALYTICS_PATH) ||
              (this.context.can('see', 'Campaign Selector')
              ? location.pathname === '/'
              : null)
            )}
            dataIntercomTarget="Analytics sidebar button"
          />
          <Can I="see" a="Account">
            <SidebarLink
              title="Accounts"
              icon={AccountIcon}
              route="/accounts"
              onClick={this.hideSidebar}
              collapsed={collapsed}
              dataIntercomTarget="Accounts sidebar button"
            />
          </Can>
        </ul>
        <div className={classes.sidebar__campaign}>
          <div
            className={`${classes.siderbar__profile__block} ${classes.siderbar__campaign__block}`}
            role="button"
            tabIndex="-1"
            onKeyDown={() => {}}
            onClick={this.handleProfileClick}
          >
            <Avatar className={classes.root}>{user.first_name.charAt(0)}</Avatar>
            <div className={classes.sidebar__campaign__name}>{getFullname(user)}</div>
            <KeyboardArrowDownRoundedIcon className={classes.avatarDropdownIcon} />
          </div>
          {campaign && campaign.id &&
            <Can I="see" a="Campaign Selector">
              <React.Fragment>
                <div className={classes.siderbar__campaign__current}>Current campaign</div>
                <div className={classes.siderbar__campaign__block}>
                  {progressPct === '100.0'
                    ? <CheckRoundedIcon color="primary" size={24} />
                    : <CircularProgress color="primary" size={24} variant="static" value={progressPct} />
                  }
                  <div className={classes.sidebar__campaign__name}>{campaign.name}</div>
                </div>
              </React.Fragment>
            </Can>
          }
        </div>
        <Popover
          id="profile-menu"
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={this.handleProfileClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
        >
          <ProfileMenuItems handleLogout={this.handleLogout} />
        </Popover>
      </div>
    );
  }
}

SidebarContent.contextType = AbilityContext;

export default compose(
  withResponsive,
  withStyles(styles, { withTheme: true }),
  connect(state => ({
    user: state.user,
    campaigns: state.clientCampaigns,
    sequences: state.sequences,
  }), {
    logout,
    getClientCampaigns,
    getSequences,
    resetCampaigns,
  }),
)(SidebarContent);


const SidebarLinkCountRotting = (props) => {
  const { count } = useCountContextCountRotting();
  return (<SidebarLink
    numberError={count}
    {...props}
  />);
};
